import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import AppRouter from "./AppRouter";
import { AppProvider } from "@/shared/providers/AppProvider";
import CustomSuccessSnackbar from "@/shared/components/overlay/snackbars/CustomSuccessSnackbar";
import CustomErrorSnackbar from "@/shared/components/overlay/snackbars/CustomErrorSnackbar";
import CustomWarningSnackbar from "@/shared/components/overlay/snackbars/CustomWarningSnackbar";
import CustomInfoSnackbar from "@/shared/components/overlay/snackbars/CustomInfoSnackbar";
import CustomDefaultSnackbar from "@/shared/components/overlay/snackbars/CustomDefaultSnackbar";
import ErrorBoundary from "@/shared/components/routing/ErrorBoundary";

// Create a client with enhanced configuration
// Type for errors that may have HTTP status
interface QueryError {
  status?: number;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors
        const queryError = error as QueryError | null;
        if (queryError?.status && queryError.status >= 400 && queryError.status < 500) return false;
        return failureCount < 3;
      },
      refetchOnWindowFocus: false, // Prevent excessive refetching
      refetchOnMount: true,
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        // Don't retry mutations on client errors
        const queryError = error as QueryError | null;
        if (queryError?.status && queryError.status >= 400 && queryError.status < 500) return false;
        return failureCount < 1; // Only retry once for mutations
      },
    },
  },
});

// Enable cache debugging in development
if (import.meta.env.DEV) {
  import("@/shared/utils/cacheInvalidation").then(({ cacheDebug }) => {
    cacheDebug.invalidationTracker(queryClient);
  });
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AppProvider>
            <SnackbarProvider
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              maxSnack={3}
              dense={false}
              preventDuplicate={false}
              autoHideDuration={4000}
              Components={{
                success: CustomSuccessSnackbar,
                error: CustomErrorSnackbar,
                warning: CustomWarningSnackbar,
                info: CustomInfoSnackbar,
                default: CustomDefaultSnackbar,
              }}
            >
              <AppRouter />
            </SnackbarProvider>
          </AppProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
